import React, { forwardRef, useEffect, useRef, useState } from "react";
import { uid } from "uid/single";
import { isImageFile, deepEq } from "@exonn/utils";
import { useComposedRefs, useControlled } from "@exonn/hooks";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum as TNS } from "src/lib/i18next/types.ts";
import { ButtonV2 } from "../ButtonV2/ButtonV2.tsx";
import { PreviewListRow } from "./PreviewListRow.tsx";
import DropIcon from "./assets/dropIcon.svg?react";
import * as styles from "./DropZoneInput.style.ts";

import type { FileInputProps } from "./DropZoneInput.types.ts";
import type { DragEvent, ChangeEvent } from "react";
import type { KeyboardEvent, ForwardedRef } from "react";
import type { DropZoneValue } from "./DropZoneInput.types.ts";

import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { removeFileFromFileList } from "@exonn/utils";
import type { TFunction } from "i18next";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";
import { InputLabel } from "src/components/InputLabel/InputLabel.tsx";

await loadTranslations(TNS.DropZoneInputComponent);

const mapFilesToValues = (files: File[], id?: string): DropZoneValue[] =>
  files.map((file) => ({
    id: id || uid(),
    tempFile: file,
    fileName: file.name,
    url: isImageFile(file) ? URL.createObjectURL(file) : undefined,
    status: id ? "replace" : "new",
  }));

const DropZoneInputComponent = (
  props: FileInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const {
    validFileParameters,
    label,
    helperText,
    validationRules,
    required,
    error,
    disabled,
    isLoading = false,
    isUploading,
    progress = 0,
    placeholder,
    // abortUpload,
    multiple = false,
    value,
    defaultValue = [],
    onUpload,
    onChange: onChangeNative,
    ...rest
  } = props;

  const accept = validationRules?.accept || [];

  const [values, setValues] = useControlled({
    name: "DropZoneInput",
    controlled: value,
    default: defaultValue,
    state: "expanded",
  });

  const [isDrag, setIsDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const composedRef = useComposedRefs(inputRef, ref);
  const { t } = useTranslation(TNS.DropZoneInputComponent);
  const inputId = uid();

  const getPlaceholderText = () => {
    if (isUploading && !!progress) return `${progress} %`;
    if (placeholder) return placeholder;
    return (
      t("dragOrChoose") +
      (accept.length > 0 ? " | " + accept.map(cleanRule(t)).join(", ") : "")
    );
  };

  const handleSetValues = (newValues: DropZoneValue[]) => {
    setValues(newValues);
    onUpload?.(newValues);
  };

  const handleFileList = (fileList: FileList) => {
    if (isUploading) return;

    const newValues = multiple
      ? [...values, ...mapFilesToValues(Array.from(fileList))]
      : mapFilesToValues(Array.from(fileList));

    handleSetValues(newValues);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDrag(true);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDrag(false);
    handleFileList(e.dataTransfer.files);
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDrag(false);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.files && handleFileList(e.target.files);
    onChangeNative?.(e);
  };

  const handleRemoveFile = (id: string) => () => {
    const updatedValues: DropZoneValue[] = values.flatMap((value) =>
      value.id === id
        ? value.status === "exist"
          ? [{ ...value, status: "delete" }]
          : []
        : [value],
    );

    if (inputRef.current) {
      const index = values.findIndex((item) => item.id === id);
      removeFileFromFileList(index, inputRef.current);
    }

    handleSetValues(updatedValues);
  };

  const handleReplaceFile = (id: string) => (newFile: File) => {
    const updatedValues: DropZoneValue[] = values.map((file) =>
      file.id === id ? mapFilesToValues([newFile], id)[0] : file,
    );

    handleSetValues(updatedValues);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleKeyDown = (ev: KeyboardEvent<HTMLDivElement>) => {
    if (ev.code === "Enter" || ev.code === "Space") {
      ev.preventDefault();
      handleClick();
    }
  };

  useEffect(() => {
    if (value && !deepEq(value, values)) {
      setValues(value);
    } else if (!value && values.length) {
      setValues([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const renderPreviewList = () => {
    const filteredValues = values.filter(({ status }) => status !== "delete");

    return !filteredValues.length ? null : (
      <div css={styles.previewListStyle}>
        {filteredValues.map(({ id, url, fileName }) => (
          <PreviewListRow
            key={id}
            url={url}
            onRemove={handleRemoveFile(id)}
            onReplace={handleReplaceFile(id)}
            imageName={fileName}
          />
        ))}
      </div>
    );
  };

  return (
    <Skeleton isLoading={isLoading}>
      <div css={styles.rootContainer}>
        {label && (
          <InputLabel
            label={label}
            required={required}
            helperText={helperText}
          />
        )}

        <div
          tabIndex={0}
          css={styles.rootDropZoneStyle({ error, disabled })}
          onDrop={handleDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onKeyDown={handleKeyDown}
        >
          <div css={styles.rootInputStyle({ progress, isUploading, isDrag })}>
            <div className="progress" />

            <DropIcon css={styles.dropIconStyle} />

            <label htmlFor={inputId}>
              <span>{getPlaceholderText()}</span>

              <input
                ref={composedRef}
                id={inputId}
                hidden
                disabled={disabled}
                type="file"
                multiple={multiple}
                onChange={handleFileChange}
                accept={accept.length > 0 ? accept.join(",") : undefined}
                {...rest}
              />
            </label>

            <ButtonV2
              tabIndex={-1}
              skin="info"
              size="sm"
              variant="filled"
              css={{ flexShrink: 0 }}
              onClick={handleClick}
              disabled={disabled}
            >
              {t("chooseBtn")}
            </ButtonV2>

            <div className="dragOver">{t("dropHere")}</div>
          </div>

          {!!values.length && renderPreviewList()}
        </div>

        {((error && !disabled) || validFileParameters) && (
          <p css={styles.helperTextStyles({ error })}>
            {error || validFileParameters}
          </p>
        )}
      </div>
    </Skeleton>
  );
};

const DropZoneInput = forwardRef(DropZoneInputComponent);
DropZoneInput.displayName = "DropZoneInput";

export { DropZoneInput };

const cleanRule = (t: TFunction) => (rule: string) => {
  const lowerCaseRule = rule.toLowerCase();

  if (lowerCaseRule === "image/*") {
    return t("anyImage");
  }

  if (lowerCaseRule === "video/*") {
    return t("anyVideo");
  }

  if (
    lowerCaseRule.startsWith("image/") ||
    lowerCaseRule.startsWith("application/") ||
    lowerCaseRule.startsWith("video/")
  ) {
    return lowerCaseRule
      .replace("image/", "")
      .replace("application/", "")
      .replace("video/", "")
      .toUpperCase();
  }

  return lowerCaseRule;
};
